import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Flex, Input, Text, Form, Button } from 'onefaceui-react-components';
import { useDispatch, useSelector } from 'react-redux';
import ReCAPTCHA from 'react-google-recaptcha';

import { actionCreators as i18nActions } from 'react/api/actions/i18n';
import descartesBrand from 'static/images/brand.svg';
import defaultFetch from 'react/api/sagas/fetch/defaultFetch';
import Loading from 'react/components/Loading/Loading';

export default function RecoverCredentials() {
  const [errorMsg, setErrorMsg] = useState('');
  const [username, setUsername] = useState('');
  const [isPending, setIsPending] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const recaptchaRef = useRef(null);
  const dispatch = useDispatch();
  const i18n = useSelector((state) => state.i18n.get('resources'));
  const isProduction = process.env.NODE_ENV === 'production';

  const {
    btnText,
    descriptionText,
    messageRecaptcha,
    messageSuccess,
    messageError,
    usernameError,
    usernameText
  } = useMemo(() => {
    return {
      descriptionText: i18n.get(
        'recoverCredentials.label.description',
        'Recover your Credentials'
      ),
      usernameText: i18n.get('authCredentials.fields.username', 'Username'),
      usernameError: i18n.get(
        'user.enter.username',
        'Please enter your username first.'
      ),
      btnText: i18n.get('recoverCredentials.button.submit', 'Recover'),
      messageRecaptcha: i18n.get('Login.CheckRecaptcha'),
      messageError: i18n.get('ForgotPassword.recoverPassword.error'),
      messageSuccess: i18n.get('ForgotPassword.email.success')
    };
  }, [i18n]);

  const finishRecover = () => {
    $.toast({
      message: messageSuccess,
      displayTime: 3000,
      inTime: 100,
      outTime: 400,
      vPosition: 'center',
      hPosition: 'center',
      className: 'toastSuccess login',
      maxWidth: 700
    });
    recaptchaRef.current?.reset();
    setTimeout(() => {
      window.location.href = '/';
    }, 3500);
  };

  const handleSubmit = async () => {
    if (!username) {
      setErrorMsg(usernameError);
      return;
    } else {
      setErrorMsg('');
    }

    if (isProduction && !recaptchaToken) {
      $.toast({
        message: messageRecaptcha,
        displayTime: 3000,
        inTime: 100,
        outTime: 400,
        vPosition: 'center',
        hPosition: 'center',
        className: 'toastError login',
        maxWidth: 700
      });
      return;
    }

    setIsPending(true);
    try {
      const body = {
        login: username
      };

      const response = await defaultFetch('/User/ForgotPassword', body, 'POST');

      if (response?.error) {
        const { errorMessages } = response.error;
        let resourceError = null;
        if (errorMessages.length > 0) {
          resourceError = errorMessages[0].resource.i18nMessage;
        }
        $.toast({
          message: resourceError ? i18n.get(resourceError) : messageError,
          displayTime: 3000,
          inTime: 100,
          outTime: 400,
          vPosition: 'center',
          hPosition: 'center',
          className: 'toastError login',
          maxWidth: 700
        });
      }
    } catch (error) {
      finishRecover();
    } finally {
      setIsPending(false);
    }
  };

  useEffect(() => {
    const url = window.location.href;
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.hash.split('?')[1]);
    const username = params.get('username');

    if (username) {
      setUsername(username);
    }

    function getResources() {
      dispatch(
        i18nActions.setLocale({ language: navigator.language.substring(0, 2) })
      );
      dispatch(
        i18nActions.fetchResourcesByKey([
          'recoverCredentials.label.description',
          'recoverCredentials.button.submit',
          'authCredentials.fields.username',
          'user.enter.username',
          'Login.CheckRecaptcha',
          'ForgotPassword.email.success',
          'Password.CannotBeChangedException',
          'ForgotPassword.NoEmailRegistered',
          'ForgotPassword.recoverPassword.error'
        ])
      );
    }

    getResources();
  }, []);

  return (
    <Flex id="RecoverCredentials" alignItems="center" justifyContent="center">
      <Flex className="container" flexDirection="column" alignItems="center">
        <img
          className="brand"
          alt="brand"
          src={descartesBrand}
          role="presentation"
          data-testid="brand-descartes"
        />
        <Form onSubmit={handleSubmit}>
          <Text size="sm" className="description" dataTestId="description">
            {descriptionText}
          </Text>
          <Flex flexDirection="column" gap={8}>
            <Input
              name="username"
              placeholder={usernameText}
              errorMsg={errorMsg}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              dataTestId="input-username"
            />
            {isProduction && (
              <ReCAPTCHA
                ref={recaptchaRef}
                className="captcha"
                sitekey="6Ld5NS0UAAAAABvTeB7cEQfHnsa3Z2U9102D8cIW"
                onChange={(response) => setRecaptchaToken(response)}
                theme="light"
              />
            )}
            <Button type="submit" dataTestId="btn-submit" disabled={isPending}>
              {isPending ? <Loading /> : btnText}
            </Button>
          </Flex>
        </Form>
      </Flex>
    </Flex>
  );
}
